// ============================================================================
// Hooks TanStack Query — ADMINISTRATION (AD-F) : onglet Questions (atelier)
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (P-ADM-2, routes/admin.ts : canAccessGestAdmin = admin SEUL) et LE VERROU
// question-coeur aussi (P-ADM-4, assertCoreInvariant au repo — la garde UI
// « verrouille » l'ecran, le serveur refuse le payload forge en 400
// core_invariant). Le bundle questions est charge par onglet (P-ADM-8) sous
// ADMIN_ROOT_KEY, PARAMETRE par survey_type + couple equipe/periode : les
// selecteurs sont de l'ETAT REACT (P-ADM-14 — jamais le <form method=GET>
// plein-reload du PoC), changer la selection re-cle la query. Les mutations
// invalident la famille admin (['gest','admin']).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { ADMIN_ROOT_KEY, type GestAdminRole } from './useAdminAudit';

// ─── Types — MIROIR EXACT des DTOs serveur (repositories/pulseQuestions.ts) ───

export type PulseSurveyType = 'member_pulse' | 'team_pulse';
export type PulseAnswerType = 'scale_1_5' | 'yes_no' | 'text';
export type PulseCoreKey = 'workload' | 'energy' | 'mood';

export const PULSE_SURVEY_TYPES: readonly PulseSurveyType[] = ['member_pulse', 'team_pulse'];
export const PULSE_ANSWER_TYPES: readonly PulseAnswerType[] = ['scale_1_5', 'yes_no', 'text'];

/** Question d'un template/surcouche (miroir AdminPulseQuestionDto). */
export interface AdminPulseQuestion {
  id: string;
  /** non-null => question coeur (scoree — rendue verrouillee, badge Coeur). */
  coreKey: PulseCoreKey | null;
  questionType: PulseAnswerType;
  prompt: string;
  label: string | null;
  low: string | null;
  high: string | null;
  required: boolean;
  sortOrder: number;
}

/** Suggestion de la banque (miroir AdminBankQuestionDto). */
export interface AdminBankQuestion {
  id: string;
  questionType: PulseAnswerType;
  prompt: string;
  label: string | null;
  low: string | null;
  high: string | null;
  category: string | null;
}

export interface AdminQuestionsResponse {
  viewer: { role: GestAdminRole; email: string };
  surveyType: PulseSurveyType;
  template: AdminPulseQuestion[];
  bank: AdminBankQuestion[];
  teams: { id: string; name: string; directorEmail: string | null }[];
  overlay: { teamId: string; period: string; questions: AdminPulseQuestion[] } | null;
}

/** Payload d'une question envoyee au serveur (les champs coeur y sont IGNORES). */
export interface PulseQuestionPayload {
  id: string;
  questionType: PulseAnswerType;
  prompt: string;
  label: string;
  low: string;
  high: string;
  required: boolean;
}

export interface BankQuestionPayload {
  surveyType: PulseSurveyType;
  questionType: PulseAnswerType;
  prompt: string;
  label: string;
  low: string;
  high: string;
  category: string;
}

// ─── Codes d'erreur metier (GA-21 — pattern adminContractErrorCode) ───────────

/**
 * Extrait le code metier VERBATIM renvoye par le serveur ({ code }), sinon un
 * code generique par statut. Mappe sur gest:admin.questions.errors.<code>.
 * Codes serveur connus : core_invariant (P-ADM-4, le verrou coeur),
 * team_notfound (surcouche), invalid (Zod), notfound (404), forbidden (403).
 */
export function adminQuestionsErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string' && code.length > 0 && !code.startsWith('GEST_')) return code;
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

// ─── Query (bundle par onglet, P-ADM-8, parametree P-ADM-14) ──────────────────

export interface AdminQuestionsParams {
  surveyType: PulseSurveyType;
  /** Couple COMPLET ou rien (coherence de scope — le serveur refuse un demi-couple). */
  teamId: string | null;
  period: string | null;
}

function questionsUrl(params: AdminQuestionsParams): string {
  const search = new URLSearchParams({ survey_type: params.surveyType });
  if (params.teamId && params.period) {
    search.set('team_id', params.teamId);
    search.set('period', params.period);
  }
  return `/api/admin/questions?${search.toString()}`;
}

/** AD-F : bundle de l'onglet QUESTIONS (template + banque + equipes + surcouche). */
export function useAdminQuestions(enabled: boolean, params: AdminQuestionsParams) {
  const overlayKey = params.teamId && params.period ? `${params.teamId}:${params.period}` : '';
  return useQuery({
    queryKey: [...ADMIN_ROOT_KEY, 'questions', params.surveyType, overlayKey],
    queryFn: () => api.get<AdminQuestionsResponse>(questionsUrl(params)),
    enabled,
    retry: false,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

function useInvalidateQuestions() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_ROOT_KEY });
  };
}

/**
 * AD-F : remplace le MODELE d'un survey_type. Le serveur re-derive les champs
 * coeur et applique assertCoreInvariant AVANT toute ecriture (P-ADM-4) — un
 * modele sans question coeur revient en 400 core_invariant.
 */
export function useSaveQuestionTemplate() {
  const invalidate = useInvalidateQuestions();
  return useMutation({
    mutationFn: (input: { surveyType: PulseSurveyType; questions: PulseQuestionPayload[] }) =>
      api.put<{ ok: boolean }>('/api/admin/questions/template', input),
    onSuccess: () => invalidate(),
  });
}

/** AD-F : remplace la SURCOUCHE d'une equipe+periode (prompts vide = effacer). */
export function useSaveQuestionOverlay() {
  const invalidate = useInvalidateQuestions();
  return useMutation({
    mutationFn: (input: {
      surveyType: PulseSurveyType;
      teamId: string;
      period: string;
      prompts: PulseQuestionPayload[];
    }) => api.put<{ ok: boolean }>('/api/admin/questions/overlay', input),
    onSuccess: () => invalidate(),
  });
}

/** AD-F : ajoute une suggestion a la banque. */
export function useAddBankQuestion() {
  const invalidate = useInvalidateQuestions();
  return useMutation({
    mutationFn: (payload: BankQuestionPayload) =>
      api.post<{ id: string }>('/api/admin/questions/bank', payload),
    onSuccess: () => invalidate(),
  });
}

/** AD-F : retire une suggestion de la banque (les copies inserees restent intactes). */
export function useRemoveBankQuestion() {
  const invalidate = useInvalidateQuestions();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      api.delete<{ ok: boolean }>(`/api/admin/questions/bank/${encodeURIComponent(input.id)}`),
    onSuccess: () => invalidate(),
  });
}
